import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "../../../configuration";
import { useSignalR } from "../../../hooks";
import { FetchError, GetMeetingsForTeamDto, GetTeamMeetingDto, MeetingAgendaItemDto, UserDto } from "../../types";
import { EditTeamMeetingDto } from "../../types/meetings/EditTeamMeetingDto";
import { useAccessToken } from "../../use-access-token";
import { queryKeys } from "../queryKeys";
import { optimisticallyUpdateQueryData } from "../utilities";

type Variables = {
  teamGuid: string;
  meetingGuid: string;
  title: string;
  description: string;
  externalCallUrl?: string;
  invitees: UserDto[];
  agendaSteps: MeetingAgendaItemDto[];
};

const useEditTeamMeetingRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editTeamMeetingRequest(variables: Variables): Promise<void> {
    const accessToken = await getAccessToken();
    const body: EditTeamMeetingDto = {
      title: variables.title,
      description: variables.description,
      externalCallUrl: variables.externalCallUrl,
      inviteeUserIds: variables.invitees.map((user) => user.userId),
      agendaItems: variables.agendaSteps,
    };

    const response = await fetch(`${configuration.apiRootUrl}/meetings/team/${variables.meetingGuid}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editTeamMeetingRequest };
};

const useEditTeamMeeting = () => {
  const { editTeamMeetingRequest } = useEditTeamMeetingRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: editTeamMeetingRequest,
    onMutate: async (variables) => {
      const cachedTeamData = await optimisticallyUpdateQueryData<GetMeetingsForTeamDto>(
        queryClient,
        queryKeys.meetings.getMeetingsForTeam(variables.teamGuid),
        (cachedData) => ({
          meetings: cachedData.meetings.map((meeting) => {
            if (meeting.guid === variables.meetingGuid) {
              return {
                ...meeting,
                title: variables.title,
                description: variables.description,
              };
            }

            return meeting;
          }),
        }),
      );

      const cachedMeetingData = await optimisticallyUpdateQueryData<GetTeamMeetingDto>(
        queryClient,
        queryKeys.meetings.getMeeting(variables.meetingGuid),
        (cachedData) => ({
          ...cachedData,
          title: variables.title,
          description: variables.description,
          externalCallUrl: variables.externalCallUrl,
          invitees: variables.invitees,
          agendaSteps: variables.agendaSteps,
        }),
      );

      return { cachedTeamData, cachedMeetingData };
    },
    onSuccess: (_data, variables) => {
      signalR.sendRefetchTeamMeetingsList(variables.teamGuid);
    },
    onError: (_error, variables, context) => {
      queryClient.setQueryData(queryKeys.meetings.getMeetingsForTeam(variables.teamGuid), context?.cachedTeamData);
      queryClient.setQueryData(queryKeys.meetings.getMeeting(variables.meetingGuid), context?.cachedMeetingData);
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.meetings.getMeeting(variables.meetingGuid));
      queryClient.invalidateQueries(queryKeys.meetings.getMeetingsForTeam(variables.teamGuid));
    },
  });
};

export { useEditTeamMeeting };
